import React, { useEffect, useRef } from "react";
import type { AuthState, RefreshMethod } from "../types";
import { tokenManager } from "../utils/token-manager";
import { debugError, debugLog, shouldDisconnectUser } from "../utils";
import { createAuthError } from "../utils";


/**
 * Initializes the authentication state by checking for an existing access/refresh token.
 * @param setState
 * @param refresh - The refresh method to use for token refresh
 */
export function useAuthInitialization(
  setState: React.Dispatch<React.SetStateAction<AuthState>>,
  refresh: RefreshMethod,
) {
  const initializationRef = useRef<boolean>(false);

  useEffect(() => {
    if (initializationRef.current) return;
    initializationRef.current = true;

    const initializeAuth = async () => {
      debugLog("Starting auth initialization");
      try {
        // In third-party iframe contexts (e.g. app.baback.co inside myshopify.com),
        // cookies are partitioned by the browser. requestStorageAccess() grants
        // access to the unpartitioned cookie jar so auth works for logged-in merchants.
        if (typeof document.requestStorageAccess === 'function' && window.top !== window) {
          try {
            await document.requestStorageAccess();
            debugLog("Storage access granted");
          } catch {
            debugLog("Storage access denied — proceeding without unpartitioned cookies");
          }
        }

        const accessToken = tokenManager.getAccessToken();
        const storageAccessGranted = typeof document.requestStorageAccess === 'function' && window.top !== window;

        if (accessToken || storageAccessGranted) {
          debugLog("Found access token or storage access granted, attempting refresh");
          try {
            const response = await refresh();

            debugLog("Auth initialization successful", { userId: response.user.id });
            tokenManager.setAccessToken(response.accessToken);

            setState(prev => ({
              ...prev,
              user: response.user,
              isAuthenticated: true,
              isLoading: false,
              isAuthenticating: false,
              error: null,
            }));
          } catch (error) {
            debugError("Auth initialization failed - refresh error", error);

            if (shouldDisconnectUser(error)) {
              // Authentication error (401/403) - clear tokens and disconnect
              debugError("Authentication error during initialization, disconnecting user");
              tokenManager.clearTokens();
              setState(prev => ({
                ...prev,
                user: null,
                isAuthenticated: false,
                isLoading: false,
                isAuthenticating: false,
                error: null,
              }));
            } else {
              // Network or server error - keep tokens but mark as not authenticated for now
              // The user will remain "logged out" until the API is available again
              debugError("Network/server error during initialization, keeping tokens for retry");
              setState(prev => ({
                ...prev,
                user: null,
                isAuthenticated: false,
                isLoading: false,
                isAuthenticating: false,
                error: null,
              }));
            }
          }
        } else {
          debugLog("No access token found, user not authenticated");
          setState(prev => ({
            ...prev,
            user: null,
            isAuthenticated: false,
            isLoading: false,
            isAuthenticating: false,
            error: null,
          }));
        }
      } catch (error) {
        debugError("Auth initialization failed", error);
        setState(prev => ({
          ...prev,
          user: null,
          isAuthenticated: false,
          isLoading: false,
          isAuthenticating: false,
          error: createAuthError(error, "Authentication initialization failed"),
        }));
      }
    };

    initializeAuth()
      .catch(console.error);
  }, [setState, refresh]);
}
